import React, { useMemo, useEffect, useRef } from "react";
import * as echarts from "echarts";
import { Card, Col, Row } from "antd";
import { useNavigate } from "react-router-dom";

const toNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const prettifyLabel = (value) =>
  String(value || "Unknown")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const EChartCard = ({ title, option, height = 320, onDataPointClick }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !option) return;

    const chart = echarts.init(chartRef.current);
    chart.setOption(option, true);

    const handleChartClick = (params) => {
      if (typeof onDataPointClick === "function") {
        onDataPointClick(params);
      }
    };

    if (typeof onDataPointClick === "function") {
      chart.on("click", handleChartClick);
    }

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      if (typeof onDataPointClick === "function") {
        chart.off("click", handleChartClick);
      }
      chart.dispose();
      window.removeEventListener("resize", handleResize);
    };
  }, [option, onDataPointClick]);

  return (
    <Card className="dashboard-section-card" title={title}>
      <div ref={chartRef} style={{ width: "100%", height }} />
    </Card>
  );
};

const buildSimpleBarOption = ({
  categories,
  values,
  color = "#1976d2",
  valueName = "Count",
  axisLabelFormatter,
}) => ({
  tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
  grid: { left: "3%", right: "3%", top: "10%", bottom: "12%", containLabel: true },
  xAxis: {
    type: "category",
    data: categories,
    axisLabel: {
      interval: 0,
      rotate: categories.length > 4 ? 25 : 0,
      formatter: axisLabelFormatter,
    },
  },
  yAxis: {
    type: "value",
    name: valueName,
  },
  series: [
    {
      type: "bar",
      data: values,
      barWidth: "45%",
      itemStyle: {
        color,
        borderRadius: [5, 5, 0, 0],
      },
    },
  ],
});

const buildGroupedBarOption = ({ categories, series }) => ({
  tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
  legend: { bottom: 0 },
  grid: { left: "3%", right: "3%", top: "10%", bottom: "16%", containLabel: true },
  xAxis: {
    type: "category",
    data: categories,
    axisLabel: {
      interval: 0,
      rotate: categories.length > 4 ? 25 : 0,
      formatter: (value) =>
        String(value || "").length > 18 ? `${String(value).slice(0, 18)}...` : value,
    },
  },
  yAxis: { type: "value", name: "Count" },
  series,
});

const buildEmptyPageWiseOption = () =>
  ({
    ...buildGroupedBarOption({
    categories: ["No Data"],
    series: [
      {
        name: "Sociair",
        type: "bar",
        data: [0],
        itemStyle: { color: "#1e88e5", borderRadius: [4, 4, 0, 0] },
      },
      {
        name: "Eko",
        type: "bar",
        data: [0],
        itemStyle: { color: "#43a047", borderRadius: [4, 4, 0, 0] },
      },
      {
        name: "AI Replied",
        type: "bar",
        data: [0],
        itemStyle: { color: "#fb8c00", borderRadius: [4, 4, 0, 0] },
      },
    ],
    }),
    legend: { show: false },
  });

const UnifiedReportSections = ({
  reportData,
  channelMsgCount = [],
  channelMsgCountByRawId = [],
}) => {
  const navigate = useNavigate();
  const generalMetrics = reportData?.generalMetrics || {};
  // const detailedActions = reportData?.detailedActions || {};
  // const aiPerformance = reportData?.aiPerformance || {};

  const pageWise = Array.isArray(generalMetrics?.pageWise) ? generalMetrics.pageWise : [];
  // const aiCards = [
  //   {
  //     key: "ai-received",
  //     title: "AI Received Messages",
  //     value: toNumber(aiPerformance?.aiReceivedTotalMessages),
  //     color: "#1976d2",
  //   },
  //   {
  //     key: "ai-replied",
  //     title: "AI Replied Messages",
  //     value: toNumber(aiPerformance?.aiRepliedTotalMessages),
  //     color: "#2e7d32",
  //   },
  //   {
  //     key: "avg-human-reply",
  //     title: "Avg Human Reply / Day",
  //     value: toNumber(aiPerformance?.avgHumanReplyInDay),
  //     color: "#d81b60",
  //   },
  //   {
  //     key: "human-equivalent",
  //     title: "Human Equivalent Workload",
  //     value: toNumber(aiPerformance?.humanEquivalentWorkload),
  //     color: "#6f42c1",
  //     precision: 2,
  //   },
  // ];

  const channelEntries = useMemo(() => {
    if (Array.isArray(channelMsgCount) && channelMsgCount.length > 0) {
      return channelMsgCount.map((item) => [
        item?._id || item?.channel || item?.name || "Unknown",
        toNumber(item?.count),
      ]);
    }

    return Object.entries(generalMetrics?.channelWiseMessages || {}).map(
      ([key, value]) => [key, toNumber(value)]
    );
  }, [channelMsgCount, generalMetrics]);

  const channelWiseOption = useMemo(() => {
    const categories = channelEntries.length > 0 ? channelEntries.map(([key]) => key) : ["No Data"];
    const values = channelEntries.length > 0 ? channelEntries.map(([, value]) => toNumber(value)) : [0];

    return buildSimpleBarOption({
      categories,
      values,
      color: "#1e88e5",
      axisLabelFormatter: (value) => prettifyLabel(value),
    });
  }, [channelEntries]);

  const handleChannelBarClick = (params) => {
    if (!params || params.componentType !== "series") return;

    const selectedChannel = params.name;
    if (!selectedChannel || selectedChannel === "No Data") return;

    navigate(`/channels?channel=${encodeURIComponent(selectedChannel)}`);
  };

  const pageWiseOption = useMemo(() => {
    if (Array.isArray(channelMsgCountByRawId) && channelMsgCountByRawId.length > 0) {
      const groupedByProfileAndChannel = {};

      channelMsgCountByRawId.forEach((item) => {
        const profileName = item?.profile_name || "Not Mentioned";
        const profileType = item?.profile_type || "Not Mentioned";
        const key = `${profileType}__${profileName}`;

        if (!groupedByProfileAndChannel[key]) {
          groupedByProfileAndChannel[key] = {
            profileName,
            profileType,
            totalCount: 0,
          };
        }

        groupedByProfileAndChannel[key].totalCount += toNumber(item?.message_count);
      });

      const groupedRows = Object.values(groupedByProfileAndChannel).filter(
        (item) => item.totalCount > 0
      );
      const channelTypes = [...new Set(groupedRows.map((item) => item.profileType))];
      const profileLabels = groupedRows.map((item) => item.profileName);

      if (!groupedRows.length) return buildEmptyPageWiseOption();

      const series = channelTypes.map((type) => ({
        name: prettifyLabel(type),
        type: "bar",
        data: groupedRows.map((row) => (row.profileType === type ? row.totalCount : 0)),
        itemStyle: {
          borderRadius: [4, 4, 0, 0],
        },
      }));

      return buildGroupedBarOption({
        categories: profileLabels,
        series,
      });
    }

    const rowsWithData = pageWise.filter(
      (item) =>
        toNumber(item.receivedInSociair) > 0 ||
        toNumber(item.receivedInEco) > 0 ||
        toNumber(item.repliedByAi) > 0
    );

    if (!rowsWithData.length) return buildEmptyPageWiseOption();

    const categories = rowsWithData.map((item) => item.pageName || item.pageId || "Unknown");

    return buildGroupedBarOption({
      categories,
      series: [
        {
          name: "Sociair",
          type: "bar",
          data: rowsWithData.map((item) => toNumber(item.receivedInSociair)),
          itemStyle: { color: "#1e88e5", borderRadius: [4, 4, 0, 0] },
        },
        {
          name: "Eko",
          type: "bar",
          data: rowsWithData.map((item) => toNumber(item.receivedInEco)),
          itemStyle: { color: "#43a047", borderRadius: [4, 4, 0, 0] },
        },
        {
          name: "AI Replied",
          type: "bar",
          data: rowsWithData.map((item) => toNumber(item.repliedByAi)),
          itemStyle: { color: "#fb8c00", borderRadius: [4, 4, 0, 0] },
        },
      ],
    });
  }, [pageWise, channelMsgCountByRawId]);

  // const detailedActionOption = useMemo(() => {
  //   const chartData = [
  //     { name: "QR Sent", value: toNumber(detailedActions?.qrSent) },
  //     { name: "Price Sent", value: toNumber(detailedActions?.priceSent) },
  //     { name: "Ad Click", value: toNumber(detailedActions?.adClick) },
  //   ];
  //
  //   return {
  //     tooltip: { trigger: "item" },
  //     legend: { bottom: 0 },
  //     series: [
  //       {
  //         name: "Actions",
  //         type: "pie",
  //         radius: ["42%", "72%"],
  //         center: ["50%", "45%"],
  //         label: { formatter: "{b}: {c}" },
  //         data: chartData,
  //       },
  //     ],
  //   };
  // }, [detailedActions]);

  return (
    <div className="dashboard-report-grid">
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <EChartCard
            title="Channel-wise Messages"
            option={channelWiseOption}
            onDataPointClick={handleChannelBarClick}
          />
        </Col>
        <Col xs={24} lg={12}>
          <EChartCard title="Page-wise Message Breakdown" option={pageWiseOption} />
        </Col>
      </Row>

      {/*
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <EChartCard title="Detailed Actions" option={detailedActionOption} />
        </Col>
        <Col xs={24} lg={10}>
          <Card className="dashboard-section-card" title="% Handled by AI (Daily)">
            <div className="dashboard-progress-wrap">
              <Progress
                type="dashboard"
                percent={Math.min(100, Math.max(0, toNumber(aiPerformance?.percentageHandledByAi)))}
                strokeColor="#2e7d32"
                format={(percent) => `${percent}%`}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {aiCards.map((card) => (
          <Col key={card.key} xs={24} sm={12} xl={6}>
            <Card className="dashboard-metric-card" bordered={false}>
              <Statistic
                title={card.title}
                value={card.value}
                valueStyle={{ color: card.color }}
                precision={card.precision}
              />
            </Card>
          </Col>
        ))}
      </Row>
      */}

    </div>
  );
};

export default UnifiedReportSections;
